import {
  CATEGORY,
  CHASING_CUSTOMERS,
  MISSING_ADDRESS,
  MISSING_EMAIL,
  MISSING_NAME,
  NET_BALANCE_ABOVE_ZERO,
  NET_BALANCE_BELOW_ZERO,
  NET_BALANCE_IS_ZERO,
  NOT_CHASING_CUSTOMERS,
  SCHEDULE,
  SCORE,
  hasFilter,
} from '../util/filters';

import ApiError from '../util/ApiError';
import ApiService from './api-service';
import batchFetch from '../util/batchFetch';
import { get } from 'lodash';
import uniq from 'lodash/uniq';

const chasingStatus = filters => {
  if (!filters) return undefined;
  const hasChasing = hasFilter(filters, CHASING_CUSTOMERS);
  const hasNotChasing = hasFilter(filters, NOT_CHASING_CUSTOMERS);
  if (hasChasing && hasNotChasing) return undefined;
  if (hasChasing) return true;
  if (hasNotChasing) return false;
  return undefined;
};

const netBalance = (filters = []) => {
  if (!filters) return [];
  const selectedPredicates = [
    hasFilter(filters, NET_BALANCE_ABOVE_ZERO) ? { operator: 'gt', value: 0 } : null,
    hasFilter(filters, NET_BALANCE_IS_ZERO) ? { operator: 'eq', value: 0 } : null,
    hasFilter(filters, NET_BALANCE_BELOW_ZERO) ? { operator: 'lt', value: 0 } : null,
  ].filter(Boolean);
  return selectedPredicates;
};

const CustomerService = {
  getCustomerFilter(filter) {
    const categoriesFilter = filter?.filters.filter(f => f.type === CATEGORY).map(f => f.label);
    const scoreFilter = filter?.filters.filter(f => f.type === SCORE).map(f => f.label);

    return {
      name: filter?.searchText,
      archived: false,
      chasing: chasingStatus(filter?.filters),
      schedule: filter?.filters.filter(f => f.type === SCHEDULE).map(f => f.data),
      categories: categoriesFilter && categoriesFilter.length > 0 ? categoriesFilter : undefined,
      ratings: scoreFilter && scoreFilter.length > 0 ? scoreFilter : undefined,
      hasMissingEmail: hasFilter(filter.filters, MISSING_EMAIL),
      hasMissingName: hasFilter(filter.filters, MISSING_NAME),
      hasMissingAddress: hasFilter(filter.filters, MISSING_ADDRESS),
      netBalance: netBalance(filter?.filters),
    };
  },
  async get({ customerId, shortCode }) {
    const body = {
      query: `query ($customerId: String!, $taskStatus: String) {
        organisation {
          contact(ContactID: $customerId) {
            _id
            rating {
              label
              score
            }
            tasks(status: $taskStatus) {
              nodes {
                _id
                type
                assignee {
                  profile {
                    firstName
                    lastName
                  }
                }
                createdDate
                rescheduledDate
                metadata {
                  invoices {
                    InvoiceID
                    InvoiceNumber
                  }
                  result
                  notes
                }
              }
            }
            customerTags
            directDebit {
              firstTimeAcceptedBecsAt,
              firstTimeAcceptedBacsAt,
              firstTimeAcceptedSepaAt,
              firstTimeAcceptedAchAt,
            },
            collectionDetails {
              notes
              options {
                latePaymentFees
              }
            }
            customerPortalDetails {
              notes
            }
            hasMissingEmail
            hasMissingAddress
            hasWrongEmail
            hasMissingName
            hasInvalidMobileNumber
            ContactPersons {
              FirstName
              LastName
              EmailAddress
              ContactID
              Labels
              Phones {
                PhoneNumber
                PhoneAreaCode
                PhoneType
                PhoneCountryCode
              }
            }
            recipients {
              regular {
                to {
                  FirstName
                  LastName
                  EmailAddress
                  ContactID
                }
                cc {
                  FirstName
                  LastName
                  EmailAddress
                  ContactID
                }
              }
              escalated {
                to {
                  FirstName
                  LastName
                  EmailAddress
                  ContactID
                }
                cc {
                  FirstName
                  LastName
                  EmailAddress
                  ContactID
                }
              }
            }
            DefaultCurrency
            Balances {
              AccountsReceivable {
                Outstanding
                Overdue
                ExtraPayments
                NetBalance
              }
            }
            Phones {
              PhoneNumber
              PhoneAreaCode
              PhoneType
              PhoneCountryCode
            }
            Addresses {
              AddressLine1
              AddressLine2
              AddressLine3
              AddressLine4
              AddressType
              City
              Region
              PostalCode
              Country
              AttentionTo
            }
            threshold {
              latestCreditPercentage
              creditLimit
              notifications {
                percentageLimit
              }
              default
            }
            accountingSoftwareUrl
            portalUrl
            FirstName
            LastName
            ContactID
            EmailAddress
            Name
            noChase
            schedule {
              _id
              name
            }
            notes {
              message
              date
              reminder
              hash
              user {
                profile {
                  firstName
                  lastName
                }
              }
              invoices {
                InvoiceID
                InvoiceNumber
              }
            }
            sentMonthlyStatements {
              _id
              email {
                date
                from {
                  name
                  address
                }
                to {
                  name
                  address
                }
                cc {
                  name
                  address
                }
                text
                subject
              }
              emails {
                date
                from {
                  name
                  address
                }
                to {
                  name
                  address
                }
                cc {
                  name
                  address
                }
                subject
                text
              }
            }
          }
          threshold {
            creditLimit
            notifications {
              percentageLimit
            }
          }
        }
      }`,
      variables: {
        customerId,
        taskStatus: 'OPEN',
      },
    };
    const { data: result } = await ApiService.orgEndpoint(shortCode, {
      extractResponseData: false,
    }).post('/', { ...body });
    const { data, errors } = result;

    const { organisation } = data;
    const { contact } = organisation;

    if ((errors && errors.length) || contact === null) {
      throw new ApiError('Something went wrong retrieving the customer!', { data: errors });
    }

    contact.defaultThreshold = organisation.threshold;

    return contact;
  },
  async getCustomerHistory({ customerId, filters = {}, shortCode }) {
    const body = {
      query: `query ($customerId: String!, $paidInvoices: Boolean) {
        organisation {
          contact(ContactID: $customerId) {
            _id
            tasks(status: "COMPLETED") {
              nodes {
                type
                completedBy {
                  profile {
                    firstName
                    lastName
                  }
                }
                category
                createdDate
                rescheduledDate
                completedDate
                dueDate
                description
                metadata {
                  invoices {
                    InvoiceID
                    InvoiceNumber
                  }
                  result
                  notes
                  recordingUrl
                }
              }
            }
            directDebit {
              firstTimeAcceptedBecsAt
              firstTimeAcceptedBacsAt
              firstTimeAcceptedSepaAt
              firstTimeAcceptedAchAt
            },
            Name
            notes {
              message
              date
              reminder
              hash
              user {
                profile {
                  firstName
                  lastName
                }
              }
              invoices {
                InvoiceID
                InvoiceNumber
              }
            }
            sentMonthlyStatements {
              _id
              email {
                date
                from {
                  name
                  address
                }
                to {
                  name
                  address
                }
                cc {
                  name
                  address
                }
                text
                subject
              }
              emails {
                date
                from {
                  name
                  address
                }
                to {
                  name
                  address
                }
                cc {
                  name
                  address
                }
                subject
                text
              }
            }
            ContactID
            invoices(paid: $paidInvoices) {
              _id
              Status
              InvoiceID
              InvoiceNumber
              dispute {
                isOpen
                note
                reason
                hash
                createdBy {
                  profile {
                    lastName
                    firstName
                  }
                }
                history {
                  isOpen
                  note
                  reason
                  hash
                  createdBy {
                    profile {
                      lastName
                      firstName
                    }
                  }
                  createdAt
                }
              }
              chaseHistory {
                date
                days
                template
                channel
                letter {
                  html
                  status
                  statusUpdatedAt
                }
                email {
                  headers {
                    messageId
                  }
                  subject
                  text
                  to {
                    address
                    name
                  }
                  cc {
                    name
                    address
                  }
                  bcc {
                    name
                    address
                  }
                  from {
                    address
                    name
                  }
                  attachments {
                    fileName
                  }
                }
                sms {
                  from
                  to
                  text
                }
                phoneCall {
                  from
                  to
                  script
                  status
                }
                chaseOpens {
                  user
                  date
                  chase
                }
                chaseBounces {
                  user
                  date
                  chase
                }
              }
              emails {
                messageId
                from {
                  address
                  name
                }
                to {
                  address
                  name
                }
                cc {
                  name
                  address
                }
                bcc {
                  name
                  address
                }
                date
                subject
                text
                attachments {
                  size
                  path
                  name
                }
                canReplyNowWithUserId
              }
            }
          }
        }
      }`,
      variables: {
        customerId,
        paidInvoices: filters?.paid,
      },
    };
    const { data: result } = await ApiService.orgEndpoint(shortCode, {
      extractResponseData: false,
    }).post('/', { ...body });
    const { data, errors } = result;
    const { organisation: contact } = data;

    if ((errors && errors.length) || contact === null) {
      throw new ApiError('Something went wrong retrieving the customer!', { data: errors });
    }

    return contact;
  },
  async getCustomerReport({ limit, skip, sort, filter = { filters: [] }, shortCode }) {
    const body = {
      query: `query ($limit: Int, $skip: Int, $sort: String, $filter: ContactFilter) {
        organisation {
          contacts(limit: $limit, skip: $skip, sort: $sort, filter: $filter) {
            nodes {
              customerReport {
                oldestDebt
                daysToPay
                lastPaymentDate
                replyRate
                lastReplyDate
              }
            }
          }
        }
      }`,
      variables: {
        searchText: filter.searchText,
        limit,
        skip,
        sort,
        missingEmailAddress: hasFilter(filter.filters, MISSING_EMAIL),
        missingNames: hasFilter(filter.filters, MISSING_NAME),
        missingAddress: hasFilter(filter.filters, MISSING_ADDRESS),
        filter: CustomerService.getCustomerFilter(filter),
      },
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);
    const { contacts } = organisation;

    return contacts;
  },
  async getCustomers({ limit, skip, sort, filter = { filters: [] }, shortCode }) {
    const body = {
      query: `query ($filter: ContactFilter, $limit: Int
        $skip: Int, $sort: String, $missingEmailAddress: Boolean, $missingAddress: Boolean,
        $missingNames: Boolean,
        ) {
        organisation {
          BaseCurrency
          name
          schedules {
            name
            _id
          }
          ShortCode
          contacts(
            limit: $limit,
            skip: $skip,
            sort: $sort,
            filter: $filter,
            missingEmailAddress: $missingEmailAddress,
            missingNames: $missingNames,
            missingAddress: $missingAddress
            ) {
            totalCount
            nodes {
              _id
              rating {
                label
                score
              }
              hasMissingEmail
              hasMissingAddress
              hasWrongEmail
              hasMissingName
              DefaultCurrency
              Balances {
                AccountsReceivable {
                  Outstanding
                  Overdue
                  ExtraPayments
                  NetBalance
                }
              }
              FirstName
              LastName
              ContactID
              EmailAddress
              Name
              noChase
              directDebit {
                firstTimeAcceptedBecsAt,
                firstTimeAcceptedBacsAt,
                firstTimeAcceptedSepaAt,
                firstTimeAcceptedAchAt,
              },
              schedule {
                _id
                name
              }
              threshold {
                creditLimit
              }
              riskReport {
                totalDebtPercentage
                creditLimitPercentage
              }
            }
          }
        }
      }`,
      variables: {
        limit,
        skip,
        sort,
        missingEmailAddress: hasFilter(filter.filters, MISSING_EMAIL),
        missingNames: hasFilter(filter.filters, MISSING_NAME),
        missingAddress: hasFilter(filter.filters, MISSING_ADDRESS),
        filter: CustomerService.getCustomerFilter(filter),
      },
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);
    return (
      organisation && {
        total: organisation.contacts.totalCount,
        contacts:
          organisation.contacts.nodes?.map((contact, index) => ({
            id: index + parseInt(skip, 10),
            _id: contact._id,
            rating: contact.rating,
            ContactID: contact.ContactID,
            FirstName: contact.FirstName,
            LastName: contact.LastName,
            Name: contact.Name,
            hasMissingEmail: contact.hasMissingEmail,
            hasWrongEmail: contact.hasWrongEmail,
            hasMissingName: contact.hasMissingName,
            noChase: contact.noChase,
            directDebit: contact.directDebit,
            Schedule: contact.schedule,
            EmailAddress: contact.EmailAddress,
            Balances: contact.Balances,
            hasUnpaidInvoices: contact.Balances?.AccountsReceivable?.Outstanding > 0,
            BalanceOutstanding: contact.Balances?.AccountsReceivable?.Outstanding ?? '-',
            BalanceOverdue: contact.Balances?.AccountsReceivable?.Overdue ?? '-',
            BalanceExtraPayments: contact.Balances?.AccountsReceivable?.ExtraPayments,
            NetBalance: contact.Balances?.AccountsReceivable?.NetBalance ?? '-',
            DefaultCurrency: contact.DefaultCurrency,
            threshold: contact.threshold,
            riskReport: contact.riskReport,
          })) || [],
      }
    );
  },
  async getQuickView({ shortCode, contactId, users }) {
    const body = {
      query: `
        query ($contactId: String!) {
          organisation {
            contact(ContactID: $contactId) {
              notes {
                message
                date
                reminder
                hash
                user {
                  profile {
                    firstName
                    lastName
                  }
                }
              }
              contactCommunications {
                ...on ContactCommunicationEmail {
                  metadata {
                    email {
                      date
                      from {
                        address
                        name
                      }
                      to {
                        address
                        name
                      }
                      subject
                      text
                      isResponse
                      message
                    }
                  }
                }
              }
            }
          }
        }
      `,
      variables: { contactId },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    const { notes, contactCommunications } = data.organisation.contact;
    const combined = [
      ...notes.map(note => ({
        ...note,
        type: 'note',
        message: note.message.replace('<', '&lt;').replace('>', '&gt;'),
      })),
      ...contactCommunications.map(email => ({
        ...email.metadata.email,
        messageId: email.metadata.email.message,
        type: 'email',
        isResponse:
          email.isResponse != null
            ? email.isResponse
            : users.findIndex(u => u.EmailAddress === get(email, 'metadata.email.from.address')) >
              -1,
      })),
    ];

    combined.forEach(item => {
      item.dateTime = new Date(item.date).getTime();
    });

    combined.sort((m1, m2) => m2.dateTime - m1.dateTime);

    return combined.slice(0, 5);
  },
  async setContactRecipient(shortCode, customerId, escalation, recipientId, type) {
    const body = {
      query: `mutation ($contactId: String!, $recipientId: String!, $escalation: Escalation!, $type: RecipientTypeType!) {
        setContactRecipient(contactId: $contactId, escalation: $escalation, recipientId: $recipientId, type: $type){
          updated
          contact {
            _id
          }
        }
      }`,
      variables: {
        contactId: customerId,
        escalation,
        recipientId,
        type,
      },
    };

    return ApiService.orgEndpoint(shortCode).post('/', { ...body });
  },
  async addNote(shortCode, contactId, message, invoiceIds, reminderDate, mentions) {
    const body = {
      query: `mutation($contactId: NonEmptyString!, $message: NonEmptyString!, $invoiceIds: [NonEmptyString], $reminderDate: Date, $mentions: [NonEmptyString]) {
        addNote(contactId: $contactId, message: $message, invoiceIds: $invoiceIds, reminder: $reminderDate, mentions: $mentions) {
          contact {
            _id
          }
        }
      }`,
      variables: {
        contactId,
        message,
        invoiceIds,
        reminderDate: reminderDate && new Date(reminderDate).toISOString(),
        mentions,
      },
    };
    return ApiService.orgEndpoint(shortCode).post('/', { ...body });
  },
  async editNote(shortCode, contactId, noteHash, message, reminderDate, mentions) {
    const body = {
      query: `mutation($contactId: NonEmptyString!, $noteHash: NonEmptyString!, $message: NonEmptyString!, $mentions: [NonEmptyString], $reminderDate: String) {
        editNote(contactId: $contactId, noteHash: $noteHash, message: $message, mentions: $mentions, reminder: $reminderDate) {
          contact {
            _id
          }
        }
      }`,
      variables: {
        contactId,
        message,
        noteHash,
        reminderDate: reminderDate && new Date(reminderDate).toISOString(),
        mentions,
      },
    };
    return ApiService.orgEndpoint(shortCode).post('/', { ...body });
  },
  async setThreshold(shortCode, contactId, useDefault, creditLimit = 0, notifications = []) {
    const body = {
      query: `mutation($contactId: String!, $threshold: ThresholdInputType, $useDefault: Boolean) {
        setContactThreshold(contactId: $contactId, threshold: $threshold, useDefault: $useDefault)
      }`,
      variables: {
        contactId,
        threshold: {
          creditLimit,
          notifications,
        },
        useDefault,
      },
    };
    return ApiService.orgEndpoint(shortCode).post('/', { ...body });
  },
  async removeThreshold(shortCode, contactId) {
    const body = {
      query: `mutation($contactId: String!) {
        unsetContactThreshold(contactId: $contactId)
      }`,
      variables: {
        contactId,
      },
    };
    return ApiService.orgEndpoint(shortCode).post('/', { ...body });
  },
  async deleteNote(shortCode, hash) {
    const body = {
      query: `mutation($hash: String!) {
        deleteNote(noteHash: $hash)
      }
      `,
      variables: {
        hash,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.deleteNote;
  },
  async getAllCustomers({ searchText, filters, shortCode }) {
    const body = {
      query: `query ($filter: ContactFilter, $missingNames: Boolean, $missingEmailAddress: Boolean) {
        organisation {
          contacts(filter: $filter, missingNames: $missingNames, missingEmailAddress: $missingEmailAddress) {
            nodes {
              _id
            }
          }
        }
      }`,
      variables: {
        filter: CustomerService.getCustomerFilter({
          filters,
          searchText,
        }),
        missingNames: hasFilter(filters, MISSING_NAME),
        missingEmailAddress: hasFilter(filters, MISSING_EMAIL),
      },
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);
    return get(organisation, 'contacts.nodes');
  },
  async getCategories(shortCode) {
    const body = {
      query: `query {
        organisation {
          contacts {
            nodes {
              customerTags
            }
          }
        }
      }`,
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);
    const tags = uniq(organisation.contacts.nodes.flatMap(c => c.customerTags));
    return tags.map((tag, index) => ({
      id: `t-${index}`,
      name: tag,
    }));
  },
  async startChasingContacts(shortCode, contactIds) {
    await batchFetch(
      ids =>
        ApiService.orgEndpoint(shortCode).post('/', {
          query: `mutation($contactIds: [String!]) {
        unpauseContacts(contactIds: $contactIds)
      }`,
          variables: {
            contactIds: ids,
          },
        }),
      contactIds,
      { toastMessage: 'Updating contacts' },
    );
  },
  async stopChasingContacts(shortCode, contactIds) {
    await batchFetch(
      ids =>
        ApiService.orgEndpoint(shortCode).post('/', {
          query: `mutation($contactIds: [String!]) {
            pauseContacts(contactIds: $contactIds)
          }`,
          variables: {
            contactIds: ids,
          },
        }),
      contactIds,
      { toastMessage: 'Updating contacts' },
    );
  },
  async getFilterCounts(shortCode) {
    const body = {
      query: `query {
        organisation {
          chasingCustomers: contacts(
            filter: {
              chasing: true,
              archived: false,
            }
          ) {
            totalCount
          }
          notChasingCustomers: contacts(
            filter: {
              chasing: false
              archived: false,
            }
          ) {
            totalCount
          }
        }
      }`,
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);
    return {
      chasingCustomers: organisation.chasingCustomers.totalCount,
      notChasingCustomers: organisation.notChasingCustomers.totalCount,
    };
  },
  async setContactCollectionDetails({ shortCode, contactId, notes }) {
    const body = {
      query: `mutation($contactId: String!, $notes: String!) {
        setContactCollectionDetails(contactId: $contactId, notes: $notes)
      }`,
      variables: {
        contactId,
        notes,
      },
    };

    await ApiService.orgEndpoint(shortCode).post('/', body);
  },
  async setContactCustomerPortalDetails({ shortCode, contactId, notes }) {
    const body = {
      query: `mutation($contactId: String!, $notes: String!) {
        setContactCustomerPortalDetails(contactId: $contactId, notes: $notes)
      }`,
      variables: {
        contactId,
        notes,
      },
    };

    await ApiService.orgEndpoint(shortCode).post('/', body);
  },
  async setSignedCollectionState({ shortCode, caseId }) {
    const body = {
      query: `mutation($collectionCaseId: String!) {
        pendCollectionCase(collectionCaseId: $collectionCaseId)
      }`,
      variables: {
        collectionCaseId: caseId,
      },
    };

    await ApiService.orgEndpoint(shortCode).post('/', body);
  },
};

export default CustomerService;
